"use client";

import { FC, PropsWithChildren, useCallback, useContext, useState } from "react";
import Countdown from "react-countdown";
import { AuthContext, ClientBFFContext } from "@finbackoffice/site-core";
import { useTranslation } from "react-i18next";
import Button from "components/base/button/Button";
import Translate from "components/base/translate/Translate";
import Input, { OnChangeHandler } from "components/base/input-field/Input";
import { Svg } from "components/base/svg/Svg";
import { AssetsDirectory } from "@finbackoffice/enums";
import classNames from "classnames";
import { UserAccountContextV2 } from "contexts";
import { RequestError } from "@finbackoffice/fe-core";
import styles from "./verification-item.module.sass";

interface IProps {
    type: string;
    verified: boolean;
    title?: string;
    successTextCentered?: boolean;
    withMargin?: boolean;
}

const VALID_CODE_LENGTH = 6;

const VerificationItem: FC<PropsWithChildren<IProps>> = ({
    children,
    type,
    title,
    verified,
    successTextCentered,
    withMargin,
}) => {
    const client = useContext(ClientBFFContext);
    const { t } = useTranslation();
    const { setToken } = useContext(AuthContext);
    const { loadProfile } = useContext(UserAccountContextV2);
    const [isVerificationCodeSent, setIsVerificationCodeSent] = useState(verified);
    const [loading, setLoading] = useState(false);
    const [tempVerificationId, setTempVerificationId] = useState("");
    const [validUntil, setValidUntil] = useState<number>();
    const [code, setCode] = useState("");
    const [error, setError] = useState("");

    const onSubmit = useCallback(async () => {
        if (code.length >= VALID_CODE_LENGTH && !loading) {
            setError("");
            setLoading(true);
            const req = {
                id: tempVerificationId,
                code: code,
            };
            if (type === "email") {
                try {
                    const response = await client.checkEmail(req);

                    if (response.token) {
                        setToken(response.token);
                        await loadProfile();
                    }
                    setLoading(false);
                } catch (err: any) {
                    const apiError: RequestError = err.response?.data;

                    if (apiError.error === "registration_error") {
                        setError(t("verification_error_wrongCode"));
                    }
                    setLoading(false);
                }
            } else if (type === "phone") {
                try {
                    const response = await client.checkPhone(req);

                    if (response.token) {
                        setToken(response.token);
                        await loadProfile();
                    }
                    setLoading(false);
                } catch (err: any) {
                    const apiError = err.response?.data;

                    if (apiError.error === "registration_error") {
                        setError(t("verification_error_wrongCode"));
                    }
                    setLoading(false);
                }
            }
        }
    }, [client, loading, setToken, t, tempVerificationId, type, loadProfile, code]);

    const handleRequestCode = useCallback(async () => {
        if (!loading) {
            setLoading(true);
            setError("");
            if (type === "email") {
                try {
                    const response = await client.validateEmail();

                    setTempVerificationId(response.id);
                    setValidUntil(new Date(response.valid_until.toString()).getTime());
                    setIsVerificationCodeSent(true);
                    setLoading(false);
                } catch (err: any) {
                    const error = err.response?.data;
                    console.error(error);
                    setLoading(false);
                }
            } else if (type === "phone") {
                try {
                    const response = await client.validatePhone();

                    setTempVerificationId(response.id);
                    setValidUntil(new Date(response.valid_until.toString()).getTime());
                    setIsVerificationCodeSent(true);
                    setLoading(false);
                } catch (err: any) {
                    const error = err.response?.data;
                    console.error(error);
                    setLoading(false);
                }
            }
        }
    }, [client, loading, type]);

    const renderContent = useCallback(({ minutes, seconds, completed }: any) => {
        if (completed) {
            return null;
        }

        return (
            <p className={styles.resendTxt}>
                <Translate
                    tid="verification_newCodeResend"
                    replace={{
                        minutes,
                        seconds,
                    }}
                />
            </p>
        );
    }, []);

    const onCompleteHandler = () => {
        setIsVerificationCodeSent(false);
    };

    const handleCodeChange: OnChangeHandler<false> = (val: string) => {
        setCode(val);
    };

    return (
        <div className={classNames(styles.block, withMargin && styles.withMargin)}>
            {title && (
                <span>
                    <Translate tid={title} />
                </span>
            )}
            {!verified ? (
                <>
                    {children}
                    <div className={styles.verifyForm}>
                        <div className={styles.verifyFormFields}>
                            <Input
                                type="number"
                                valueAsNumber={false}
                                onChange={handleCodeChange}
                                value={code}
                                name="code"
                                disabled={!isVerificationCodeSent}
                                label={t("verification_verificationCode")}
                            />

                            {!isVerificationCodeSent && (
                                <Button
                                    type="button"
                                    loading={loading}
                                    disabled={loading}
                                    variant="secondary"
                                    onClick={handleRequestCode}>
                                    <Translate tid="verification_requestCode" />
                                </Button>
                            )}
                            {isVerificationCodeSent && (
                                <Button
                                    type="button"
                                    loading={loading}
                                    disabled={code.length < VALID_CODE_LENGTH || loading}
                                    className={styles.verifyBtn}
                                    onClick={onSubmit}>
                                    <Translate tid="verification_verify" />
                                </Button>
                            )}
                            {isVerificationCodeSent && !error && (
                                <Countdown
                                    date={validUntil}
                                    renderer={renderContent}
                                    onComplete={onCompleteHandler}
                                />
                            )}
                        </div>

                        {error && <p className={styles.wrongTxt}>{error}</p>}
                    </div>
                </>
            ) : (
                <section
                    className={classNames(
                        styles.successTxt,
                        successTextCentered && styles.alignCenter,
                    )}>
                    <Svg
                        className="svg-icon"
                        src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/account-verified.svg`}
                    />
                    <Translate tid={`verification_${type}SuccessMsg`} />
                </section>
            )}
        </div>
    );
};
export default VerificationItem;
